/**
 * Time helpers. Every displayed time carries a time zone label, and log times use the carrier's
 * home terminal time zone (never the browser's).
 */

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string, opts: Intl.DateTimeFormatOptions) {
  const key = tz + JSON.stringify(opts);
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts });
    fmtCache.set(key, f);
  }
  return f;
}

export function tzAbbrev(tz: string, at: Date | string = new Date()): string {
  const d = typeof at === 'string' ? new Date(at) : at;
  const part = fmt(tz, { timeZoneName: 'short' })
    .formatToParts(d)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? tz;
}

/** "14:05:09 CDT" */
export function formatTime(iso: string | Date, tz: string, opts: { seconds?: boolean } = {}): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const t = fmt(tz, { hour: '2-digit', minute: '2-digit', second: opts.seconds ? '2-digit' : undefined, hourCycle: 'h23' }).format(d);
  return `${t} ${tzAbbrev(tz, d)}`;
}

/** "Oct 8, 2026 14:05 CDT" */
export function formatDateTime(iso: string | Date | null | undefined, tz: string): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const s = fmt(tz, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
  return `${s} ${tzAbbrev(tz, d)}`;
}

/** "Thu, Oct 8, 2026" for a YYYY-MM-DD date (no time zone shift). */
export function formatDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

export function formatShortDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'numeric', day: 'numeric' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

/** Wall-clock parts of an instant in a zone. */
export function zonedParts(at: Date, tz: string) {
  const parts = fmt(tz, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24, minute: get('minute'), second: get('second') };
}

/** Offset (minutes, east positive) of `tz` at instant `at`. */
export function tzOffsetMinutes(at: Date, tz: string): number {
  const p = zonedParts(at, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

/** The UTC instant of a wall-clock time (HH:MM or HH:MM:SS) in `tz` (handles DST by re-checking the offset). */
export function zonedTimeToUtc(date: string, hhmm: string, tz: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm, ss = 0] = hhmm.split(':').map(Number) as [number, number, number?];
  const guess = Date.UTC(y, m - 1, d, hh, mm, ss);
  let off = tzOffsetMinutes(new Date(guess), tz);
  let t = guess - off * 60000;
  const off2 = tzOffsetMinutes(new Date(t), tz);
  if (off2 !== off) {
    off = off2;
    t = guess - off * 60000;
  }
  return new Date(t);
}

/** The home-terminal log date (YYYY-MM-DD) containing instant `at`, given the 24-hour period start. */
export function logDateFor(at: Date, tz: string, periodStart = '00:00'): string {
  const p = zonedParts(at, tz);
  const [ph, pm] = periodStart.split(':').map(Number) as [number, number];
  let date = isoDate(p.year, p.month, p.day);
  if (p.hour * 60 + p.minute < ph * 60 + pm) date = addDays(date, -1);
  return date;
}

export function isoDate(y: number, m: number, d: number) {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return isoDate(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

export function isIsoDate(s: unknown): s is string {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/** "10:45" from minutes (h:mm, hours may exceed 24). Negative values get a leading minus. */
export function formatDuration(minutes: number): string {
  const neg = minutes < 0;
  const m = Math.round(Math.abs(minutes));
  return `${neg ? '−' : ''}${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/** "3 min ago" style relative time with an absolute title fallback. */
export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'never';
  const s = Math.round((now - new Date(iso).getTime()) / 1000);
  if (Math.abs(s) < 45) return 'just now';
  const m = Math.round(s / 60);
  if (Math.abs(m) < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (Math.abs(h) < 36) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

/** Common US/Canada IANA zones for selects (any valid IANA zone is accepted by the backend). */
export const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Detroit',
  'America/Indiana/Indianapolis',
  'America/Chicago',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Puerto_Rico',
  'America/Halifax',
  'America/Toronto',
  'America/Winnipeg',
  'America/Edmonton',
  'America/Vancouver',
];

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Local calendar date and HH:MM:SS of an instant in `tz`. */
export function zonedDateTime(at: Date | string, tz: string): { date: string; time: string } {
  const p = zonedParts(typeof at === 'string' ? new Date(at) : at, tz);
  return {
    date: isoDate(p.year, p.month, p.day),
    time: `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}:${String(p.second).padStart(2, '0')}`,
  };
}
